import { useState } from "react";
import { FlaskConical } from "lucide-react";
import { MessageAction } from "@/components/ai-elements/message";
import { TestThisReplyDialog } from "@/components/AgentTests/TestThisReplyDialog";
import { ContextTurn } from "@/components/AgentTests/ContextTurns";
import { WORKSPACE_PERMISSIONS } from "@/constants/permissions";
import { usePermissions } from "@/hooks/usePermissions";
import { useBetaFeature } from "@/components/Admin/BetaFeatures/useBetaFeature";
import type { NoxusUIMessage } from "./noxus-message-types";
import { getTextFromParts, MessagePart } from "./utils";

type ActionProps = {
  agentId: string;
  conversationId: string;
  dbMessageId?: string;
  parts: MessagePart[];
  messageIndex: number;
  messages: NoxusUIMessage[];
};

export function TestThisReplyAction(props: ActionProps) {
  const agentTestsEnabled = useBetaFeature("agent_tests");
  const {
    agentId,
    conversationId,
    dbMessageId,
    parts,
    messageIndex,
    messages,
  } = props;
  const nextMessage = messages[messageIndex + 1];
  const userIndex = messages
    .slice(0, messageIndex)
    .findLastIndex((message) => message.role === "user");
  if (
    !agentTestsEnabled ||
    !agentId ||
    !conversationId ||
    !dbMessageId ||
    userIndex === -1 ||
    messages[userIndex].parts.some((part) => part.type === "file") ||
    (nextMessage && nextMessage.role !== "user") ||
    !parts.some((part) => part.type === "text")
  ) {
    return null;
  }
  return (
    <TestThisReplyButton
      {...props}
      userIndex={userIndex}
      messageId={dbMessageId}
    />
  );
}

function TestThisReplyButton({
  agentId,
  conversationId,
  messages,
  userIndex,
  messageId,
}: ActionProps & { userIndex: number; messageId: string }) {
  const { hasPermission } = usePermissions();
  const [isOpen, setIsOpen] = useState(false);
  if (!hasPermission(WORKSPACE_PERMISSIONS.EDIT, "group")) {
    return null;
  }

  const context: ContextTurn[] = messages
    .slice(0, userIndex)
    .map((message) => ({
      role: message.role === "user" ? ("user" as const) : ("agent" as const),
      text: getTextFromParts(message.parts, "\n\n"),
    }))
    .filter((turn) => turn.text.trim());

  return (
    <>
      <MessageAction
        label="Test this reply"
        tooltip="Test this reply"
        onClick={() => setIsOpen(true)}
      >
        <FlaskConical className="size-4 stroke-ring" />
      </MessageAction>
      {isOpen && (
        <TestThisReplyDialog
          assistantId={agentId}
          conversationId={conversationId}
          messageId={messageId}
          userMessage={getTextFromParts(messages[userIndex].parts, "\n\n")}
          context={context}
          onClose={() => setIsOpen(false)}
        />
      )}
    </>
  );
}
